import {Injectable}                                              from '@angular/core';
import {ApElementType}                                           from '../../ap-interface/enums/ap-elements-type.enum';
import {FertilizerStore}                                         from '../../stores/base-data/fertilizer.store';
import {IBaseFertStatistic, RemainDemandOption, RemainingDemand} from '../../types/base-fertilization.types';
import {FertilizerService}                                       from './fertilizer.service';
import {ObjectFactory}                                           from 'ts-tooling';
import {SampleDateObject}                                        from '../../stores/nutrients/soilsampledate.store';
import {
  IGridStatus
}                                                                from '../../ap-interface/interfaces/ap-grid-status.interface';
import {ApDateService}                                           from '../../ap-core/services/ap-date-service';
import {CampaignYearService}                                     from './campaign-year.service';
import {GetElementService}                                       from '../../ap-utils/service/ap-get-element.service';
import {ApElementOxydTypeEnum}                                   from '../../ap-interface';
import {
  ApNutrientStatisticService
}                                                                from '../../nutrient-management/service/ap-nutrient-statistic.service';
import IFieldNutrientPlanningStatistic = Data.Nutrients.IFieldNutrientPlanningStatistic;
import INutrientFertilisationStatisticValue = Data.TaskManagement.INutrientFertilisationStatisticValue;
import ISampleRegion = Data.Nutrients.ISampleRegion;
import ICropRotationAttributes = Data.FieldManagement.ICropRotationAttributes;
import IRbStatistic = Data.Nutrients.IRbStatistic;
import ICampaignYear = Data.Authentication.ICampaignYear;

@Injectable({providedIn: 'root'})
export class BaseFertilizationService {
  constructor(private fertilizerStore: FertilizerStore,
              private dateService: ApDateService,
              private elementService: GetElementService,
              private fertilizerService: FertilizerService,
              private campaignYearService: CampaignYearService,
              private nutrientStatisticService: ApNutrientStatisticService) {
  }

  public getGDStatusByCrAttribute(sampleDateObject: SampleDateObject, sampleRegions: ISampleRegion[],
                                  selectedCampaignYear: ICampaignYear, crAttribute?: ICropRotationAttributes): IGridStatus {
    const sampleDateMidnight = sampleDateObject?.Date ? this.dateService.getDateMidnight(sampleDateObject.Date) : undefined;
    const soilSampleYearWithInterval = this._getEndOfCampaignYearWithSoilSampleInterval(sampleDateObject, sampleRegions);
    const hasNoSampleDate = !sampleDateObject?.Date;
    const hasNoCropInCurrentYear = !crAttribute || crAttribute.IsCropClosed === false;
    const hasNoWsvRaster = !crAttribute || crAttribute.HasWsvRaster === false;
    const hasNoIrRaster = !crAttribute || crAttribute.HasIrRaster === false;
    const hasNoRbRaster = !crAttribute || crAttribute.HasRbRaster === false;
    const sampleDateInFutureCampaignYear = sampleDateMidnight > new Date(selectedCampaignYear.DefaultEnd);
    const soilSampleWithIntervalOlderThenCurrentYear = !!soilSampleYearWithInterval
      && soilSampleYearWithInterval < new Date(selectedCampaignYear.DefaultStart);
    if (hasNoSampleDate || sampleDateInFutureCampaignYear || soilSampleWithIntervalOlderThenCurrentYear) {
      // grey Status
      return {
        state: 2,
        class: 'ap-status-grey',
        description: 'Nutrient_Planning__No_Sample_Date_Found'
      };
    } else if (hasNoCropInCurrentYear) {
      // yellow Status
      return {
        state: 0,
        class: 'ap-status-yellow',
        description: 'Nutrient_Planning__No_Crop_In_Current_Year'
      };
    } else if (hasNoWsvRaster || hasNoIrRaster || hasNoRbRaster) {
      // red Status
      return {
        state: -1,
        class: 'ap-status-red',
        description: 'Global__Please_Contact_The_Support'
      };
    }
    // green Status
    return {
      state: 1,
      class: 'ap-status-green',
      description: 'Global__Variable_Planning_Possible'
    };
  }

  public getRemainDemands(gdStatus: IGridStatus, statistics: IRbStatistic | undefined, type: RemainDemandOption): RemainingDemand {
    if (gdStatus?.state === 1 && !!statistics && Object.keys(statistics).length > 0) {
      if (type === RemainDemandOption.First) {
        return {
          P: this.elementService.CalculateElementOxidValueByGivenElementValue(
            this.nutrientStatisticService.getAverageRb(0, statistics),
            ApElementOxydTypeEnum.P
          ),
          K: this.elementService.CalculateElementOxidValueByGivenElementValue(
            this.nutrientStatisticService.getAverageRb(1, statistics),
            ApElementOxydTypeEnum.K
          ),
          Mg: this.elementService.CalculateElementOxidValueByGivenElementValue(
            this.nutrientStatisticService.getAverageRb(2, statistics),
            ApElementOxydTypeEnum.Mg
          ),
          CaO: this.nutrientStatisticService.getAverageRb(3, statistics),
          N: 0
        };
      }
      if (type === RemainDemandOption.Second) {
        return {
          P: this.elementService.CalculateElementOxidValueByGivenElementValue(
            this.nutrientStatisticService.getAverageRb(4, statistics),
            ApElementOxydTypeEnum.P
          ),
          K: this.elementService.CalculateElementOxidValueByGivenElementValue(
            this.nutrientStatisticService.getAverageRb(5, statistics),
            ApElementOxydTypeEnum.K
          ),
          Mg: this.elementService.CalculateElementOxidValueByGivenElementValue(
            this.nutrientStatisticService.getAverageRb(6, statistics),
            ApElementOxydTypeEnum.Mg
          ),
          CaO: this.nutrientStatisticService.getAverageRb(7, statistics),
          N: 0
        };
      }
    }
    return {
      P: undefined,
      K: undefined,
      Mg: undefined,
      CaO: undefined,
      N: undefined
    };
  }

  public calculateNElementByLeadElement(productId: number | null | undefined,
                                        statistics: IFieldNutrientPlanningStatistic[] | null | undefined): IBaseFertStatistic | null {
    if (!productId || !statistics || statistics.length <= 0) {
      return null;
    }
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return null;
    }
    const leadNutrient = this.fertilizerService.getFertilizerLeadingElement(fertilizer) ?? ApElementType.P;
    const leadNutrientStatistic = this._getStatisticValue(statistics.find(_ => _.Band === leadNutrient - 1));
    if (!leadNutrientStatistic) {
      return null;
    }
    const leadFertilizerContent = this.fertilizerService.getFertilizerContent(fertilizer, leadNutrient, true);
    const factor = fertilizer.ContentN / leadFertilizerContent;
    if (!Number.isFinite(factor)) {
      return null;
    }
    leadNutrientStatistic.Min = leadNutrientStatistic.Min * factor;
    leadNutrientStatistic.Max = leadNutrientStatistic.Max * factor;
    leadNutrientStatistic.Average = leadNutrientStatistic.Average * factor;
    return leadNutrientStatistic;
  }

  public addProductAmountStatistic(productId: number | null | undefined, targetElement: number | null | undefined,
                                   statistics: INutrientFertilisationStatisticValue[] | null | undefined): INutrientFertilisationStatisticValue[] | null {
    if (!productId || !statistics || statistics.length <= 0) {
      return null;
    }
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return null;
    }
    const leadNutrient = this.fertilizerService.getFertilizerLeadingElement(fertilizer) ?? ApElementType.P;
    const leadFertilizerContent = this.fertilizerService.getFertilizerContent(fertilizer, targetElement ?? leadNutrient);

    const stat = statistics.find(_ => _.ElementId === (targetElement ?? leadNutrient));
    const newStat = ObjectFactory.Copy(stat);
    newStat.Min = stat.Min / leadFertilizerContent;
    newStat.Max = stat.Max / leadFertilizerContent;

    // Corner case for negative content fertilizers
    if (newStat.Min > newStat.Max) {
      const tempMin = newStat.Min;
      newStat.Min = newStat.Max;
      newStat.Max = tempMin;
    }
    newStat.Average = stat.Average / leadFertilizerContent;
    newStat.ElementId = -stat.ElementId;
    statistics.unshift(newStat);

    return statistics;
  }

  public calculateProductAmount(productId: number, targetElement: number | null | undefined, elementValue: number): number {
    if (!productId) {
      return null;
    }
    const fertilizer = this.fertilizerStore.getFertilizer(productId);
    if (!fertilizer) {
      return null;
    }
    const leadNutrient = this.fertilizerService.getFertilizerLeadingElement(fertilizer) ?? ApElementType.P;
    const leadFertilizerContent = this.fertilizerService.getFertilizerContent(fertilizer, targetElement ?? leadNutrient);

    return Math.abs(elementValue / leadFertilizerContent);
  }

  private _getStatisticValue(statistic: {
    Min: number,
    Max: number,
    Mean: number
  } | null | undefined): IBaseFertStatistic {
    if (statistic && (statistic.Mean > 0 || statistic.Max > 0 || statistic.Min > 0)) {
      return {
        Min: statistic.Min,
        Max: statistic.Max,
        Average: statistic.Mean
      };
    }
    return null;
  }

  private _getEndOfCampaignYearWithSoilSampleInterval(sampleDateObject: SampleDateObject,
                                                      sampleRegions: ISampleRegion[]): Date | undefined {
    if (!sampleDateObject || !sampleDateObject.Date
      || !sampleDateObject.Region || !sampleRegions || sampleRegions.length === 0) {
      return undefined;
    }
    const sampleRegion = sampleRegions.find(x => x.Region === sampleDateObject.Region);
    if (!Number.isFinite(sampleRegion?.SoilSampleInterval)) {
      return undefined;
    }
    const soilSampleInterval = !sampleRegion.SoilSampleInterval ? 0 : sampleRegion.SoilSampleInterval;
    const soilSampleCampaignYearStart = this.campaignYearService.getCampaignYearBeginFromSampleDate(sampleDateObject.Date);
    const soilSampleLastCampaignYear = this.campaignYearService.getCampaignYearRange(
      soilSampleCampaignYearStart.getFullYear() + soilSampleInterval
    );
    return soilSampleLastCampaignYear.EndYear;
  }
}
